import flatpickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
import { isSiteCanadian } from 'src/utils/helpers';
import React, { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import '../styles.scss';

interface IDatePickerInputProps {
    id?: string;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    hasError?: boolean;
    required?: boolean;
    selectedDate?: string | null;
    handleOnClose: (date: string) => void;
    minDate?: Date;
    maxDate?: Date;
    disabled?: boolean;
}

/*eslint no-magic-numbers: ["error", { "ignore": [0, 1, 2, 3] }]*/
const MONTH_ADJUSTMENT = 1;
const PADDING_LENGTH = 2;
const REGEX_YEAR_INDEX = 1;
const REGEX_MONTH_INDEX = 2;
const REGEX_DAY_INDEX = 3;
const HOURS_IN_DAY = 0;
const MINUTES_IN_HOUR = 0;
const SECONDS_IN_MINUTE = 0;
const MILLISECONDS_IN_SECOND = 0;

const parseYMDToLocalDate = (value?: string | null): Date | undefined => {
    if (!value || typeof value !== 'string') return undefined;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return undefined;

    const year = Number(match[REGEX_YEAR_INDEX]);
    const month = Number(match[REGEX_MONTH_INDEX]) - MONTH_ADJUSTMENT;
    const day = Number(match[REGEX_DAY_INDEX]);

    return new Date(year, month, day);
};

const formatLocalDateToYMD = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + MONTH_ADJUSTMENT}`.padStart(
        PADDING_LENGTH,
        '0',
    );
    const day = `${date.getDate()}`.padStart(PADDING_LENGTH, '0');

    return `${year}-${month}-${day}`;
};

export const DatePickerInput: React.FC<IDatePickerInputProps> = ({
    id = 'datepicker-future',
    ariaLabel,
    ariaDescribedBy,
    hasError,
    required,
    selectedDate,
    handleOnClose,
    disabled = false,
    minDate = (() => {
        const d = new Date();
        d.setHours(
            HOURS_IN_DAY,
            MINUTES_IN_HOUR,
            SECONDS_IN_MINUTE,
            MILLISECONDS_IN_SECOND,
        );
        return d;
    })(),
    maxDate,
}) => {
    const fp1 = useRef() as MutableRefObject<Instance | null>;
    const inputElementRef = useRef<HTMLInputElement | null>(null);

    const inputRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (!node) return;

            const input = node.querySelector(
                '[data-input]',
            ) as HTMLInputElement | null;

            inputElementRef.current = input;

            if (fp1.current) {
                fp1.current.destroy();
                fp1.current = null;
            }

            const initialDate = parseYMDToLocalDate(selectedDate);

            fp1.current = flatpickr(node, {
                wrap: true,
                static: false,
                clickOpens: true,
                allowInput: true,
                appendTo: document.body,
                positionElement: input || undefined,
                dateFormat: isSiteCanadian() ? 'd/m/Y' : 'm/d/Y',
                ariaDateFormat: isSiteCanadian() ? 'd/m/Y' : 'm/d/Y',
                mode: 'single',
                nextArrow:
                    '<i class="c-icon c-icon--[semantic-forward] c-icon--simple"></i>',
                prevArrow:
                    '<i class="c-icon c-icon--[semantic-back] c-icon--simple"></i>',
                minDate,
                maxDate,
                monthSelectorType: 'static',
                defaultDate: initialDate,
                onClose: (selectedDates) => {
                    if (!selectedDates?.[0]) {
                        handleOnClose('');
                        return;
                    }

                    handleOnClose(formatLocalDateToYMD(selectedDates[0]));
                },
            });

            if (initialDate) {
                fp1.current.setDate(initialDate, false);
            } else {
                fp1.current.clear();
                if (inputElementRef.current) {
                    inputElementRef.current.value = '';
                }
            }
        },
        [handleOnClose, minDate, maxDate, selectedDate],
    );

    useEffect(() => {
        if (!fp1.current) return;

        const parsedDate = parseYMDToLocalDate(selectedDate);

        if (parsedDate) {
            fp1.current.setDate(parsedDate, false);
        } else {
            fp1.current.clear();
            if (inputElementRef.current) {
                inputElementRef.current.value = '';
            }
        }
    }, [selectedDate]);

    useEffect(() => {
        return () => {
            if (fp1.current) {
                fp1.current.destroy();
                fp1.current = null;
            }
        };
    }, []);

    return (
        <div
            className='c-datepicker js-datepicker date-picker'
            data-datepicker-month-selector-type='static'
        >
            <div className='c-input' ref={inputRef}>
                <input
                    className='c-input__input'
                    id={id}
                    aria-label={ariaLabel}
                    aria-describedby={ariaDescribedBy}
                    aria-invalid={hasError}
                    aria-required={required}
                    required={required}
                    type='text'
                    data-input
                    placeholder='Select Date'
                    disabled={disabled}
                />
                <button
                    className='c-input__addon c-input__addon--no-background'
                    tabIndex={-1}
                    aria-label='Toggle Datepicker'
                    type='button'
                    data-toggle
                    disabled={disabled}
                >
                    <i
                        className='c-icon c-icon--[semantic-calendar]'
                        aria-hidden='true'
                        role='img'
                    />
                </button>
            </div>
        </div>
    );
};
